import { CircleLoader } from "@/components/shared/loaders";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { MarginDecisionEnum } from "@/graphql/__generated__/graphql";
import { Dispatch, JSX, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

interface Props {
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
  userDecision: MarginDecisionEnum;
  loading?: boolean;
  actionFn?: () => void;
  isSkip?: boolean;
  awaitingOrganizationName?: string;
}

/**
 * DecisionConfirmationAlertDialog component renders an alert dialog to confirm the user's decision.
 *
 * @param {Props} props - The properties passed to the component.
 * @param {boolean} props.open - Determines if the alert dialog is open.
 * @param {function} props.onOpenChange - Callback function to handle the change in the open state of the dialog.
 * @param {DecisionActionEnum} props.userDecision - The decision action taken by the user.
 * @param {boolean} props.loading - Loading flag for the submitDecision mutation.
 *
 * @returns {JSX.Element} The rendered alert dialog component.
 */
const DecisionConfirmationAlertDialog = (props: Props): JSX.Element => {

  const { open, onOpenChange, userDecision, loading, actionFn, isSkip, awaitingOrganizationName } = props;

  // Hooks
  const { t } = useTranslation(["common", "modals"]);

  /**
   * Returns a confirmation text based on the user's decision.
   *
   * @param {DecisionActionEnum} userDecision - The decision action made by the user.
   * @returns {string} The confirmation text corresponding to the user's decision.
   *
   * @remarks
   * - If the user decision is `Approve`, the function returns a message indicating that the user is about to agree with the decision and that the action cannot be undone.
   * - If the user decision is `Reject`, the function returns a message indicating that the user is about to reject the decision and that the action cannot be undone.
   */
  const getDecisionText = (userDecision: MarginDecisionEnum): string => {
    let prefix = "";
    if (isSkip && userDecision === MarginDecisionEnum.Approve) {
      prefix = t("modals:margindecision.skipapprovetext", { organizationName: awaitingOrganizationName });
    }
    else if (isSkip && userDecision === MarginDecisionEnum.Reject) {
      prefix = t("modals:margindecision.skiprejecttext", { organizationName: awaitingOrganizationName });
    }
    else if(userDecision === MarginDecisionEnum.Approve) {
      prefix = t("modals:margindecision.approvetext");
    }
    else if(userDecision === MarginDecisionEnum.Reject) {
      prefix = t("modals:margindecision.rejecttext");
    }
    else if(userDecision === MarginDecisionEnum.Request) {
      prefix = t("modals:margindecision.requesttext");
    }
    return `${prefix} ${t("modals:biddecision.disclaimer")}`;
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="select-none">
        <AlertDialogHeader>
          <AlertDialogTitle>{t("modals:biddecision.title")}</AlertDialogTitle>
          <AlertDialogDescription>
            {getDecisionText(userDecision)}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel autoFocus={false} onKeyDown={(e) => e.preventDefault()}>
            {t("buttons.cancel")}
          </AlertDialogCancel>
          <Button
            autoFocus={false}
            className={cn("gap-2", loading && "cursor-wait")}
            type={loading ? "button" : "submit"}
            form={userDecision === MarginDecisionEnum.Request ? "" : "margin-decision-form"}
            disabled={loading}
            onKeyDown={(e) => e.preventDefault()}
            onClick={() => {
              if(userDecision === MarginDecisionEnum.Request && actionFn) {
                return actionFn();
              }
            }}
          >
            <CircleLoader visible={loading} height={16} width={16} strokeWidth={2}/>
            {loading ? (
              t("buttons.submitting")
            ) : (
              t("buttons.submit")
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DecisionConfirmationAlertDialog;
